import React, {useCallback, useEffect, useRef, useState} from "react";
import {Grid2, useTheme} from "@mui/material";
import {orderApi, quoteApi} from "@/boxhub-api";
import OrderBanner from "@/common/order/OrderBanner";
import {OrderDTO, QuoteDTO, QuoteStatus, RequestActions} from "@/api/data-contracts";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {AppPaths} from "@/app/AppPaths";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {AppFrame} from "@/app/AppFrame";
import {useSnackbarEx} from "@/lib/snackbarex";
import {useDialogController} from "@/lib/dialog-controller";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import EmailHistoryDialog from "@/common/EmailHistoryDialog";

import {useModelState, ValidatorOptions} from "@/lib/modelstate";
import {quoteReducer} from "@/model/quote";
import {useQuoteSchema} from "@/pages/external-request-page/schema";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {AxiosError, AxiosResponse} from "axios";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import NotificationsWithPanel from "@/common/notifications/NotificationsWithPanel";
import {OrderDocumentsCard} from "@/common/request-cards/OrderDocumentsCard";
import {useTranslation} from "react-i18next";
import {CommonI18N, NotificationI18N, RequestI18N} from "@/translations";
import {ProductCard} from "@/common/request-cards/ProductCard";
import {PalletizingCard} from "@/common/request-cards/PalletizingCard";
import {PricingCard} from "@/common/request-cards/PricingCard";
import {QuoteCard} from "@/common/request-cards/QuoteCard";
import {RequestCard} from "@/common/request-cards/RequestCard";
import {transformToErrorShape} from "@/lib/accessor";
import {BriefIcon} from "@/agentur/AgenturIcons";
import ExternalRequestBanner from "@/common/request-details/ExternalRequestBanner";

const ExternalRequestPage: React.FC = () => {
    const { t } = useTranslation();
    const params = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const schema = useQuoteSchema("quote");
    const { showMessage } = useSnackbarEx();
    const requestId = params.id ? Number.parseInt(params.id, 10) : undefined;
    const [DialogNode, showDialog] = useDialogController();
    const theme = useTheme();
    const [processing, setProcessing] = useState(false);
    const [skipUnsavedCheck, setSkipUnsavedCheck] = useState(false);
    const [allVersions, setAllVersions] = useState<QuoteDTO[]>([]);
    const [versionIdx, setVersionIdx] = useState<number>(0);
    const [editingRevision, setEditingRevision] = useState(false);
    const [order, setOrder] = useState<OrderDTO | null>(null);

    const fetchOrder = useCallback(async (reqId: number) => {
        try {
            const response = await orderApi.getByRequest(reqId);
            // 204 No Content means no order yet
            setOrder(response.data ?? null);
        } catch {
            setOrder(null);
        }
    }, []);

    // Fetch Quote
    const fetchQuote = async (id: number): Promise<AxiosResponse<QuoteDTO, any>> => {
        if (id == null) {
            throw new Error("Request ID is missing");
        }
        return quoteApi.getOrCreateQuote({ requestId: id });
    };

    const errorRef = useRef({ key: "", error: {} });

    const validator = async (model: QuoteDTO, options: ValidatorOptions<QuoteDTO>) => {
        if (!model.requestDisplay || !model.orderData) {
            return errorRef.current.error;
        }
        const newKey = `${model.requestDisplay.validUntil}-${model.requestDisplay.deliveryDateFrom}-${model.requestDisplay.deliveryDateTo}-${model.validUntil}-${model.orderData.deliveryDateFrom}-${model.orderData.deliveryDateTo}`;
        if (errorRef.current.key !== newKey) {
            errorRef.current.key = newKey;
            const response = await quoteApi.validateQuoteDates({
                validUntilRequest: model.requestDisplay.validUntil,
                deliveryDateFromRequest: model.requestDisplay.deliveryDateFrom,
                deliveryDateToRequest: model.requestDisplay.deliveryDateTo,
                validUntilQuote: model.validUntil,
                deliveryDateFromQuote: model.orderData.deliveryDateFrom,
                deliveryDateToQuote: model.orderData.deliveryDateTo
            });
            errorRef.current.error = transformToErrorShape(response.data?.error?.errors);
        }
        return errorRef.current.error;
    };

    const { model, errors, onChange, status, isDirty, dispatch, validate, setErrors, hasErrors, httpErrorStatus } =
        useModelState<QuoteDTO>({
            initialState: {} as any,
            reducer: quoteReducer,
            schema,
            id: requestId == null ? null : +requestId,
            loader: fetchQuote,
            validator
        });

    // Load all quote versions for version navigation pagination
    useEffect(() => {
        if (requestId == null) return;
        fetchOrder(requestId);
    }, [requestId, fetchOrder]);

    useEffect(() => {
        if (requestId == null) return;
        const loadVersions = async () => {
            try {
                const res = await quoteApi.getQuoteVersions({requestId});
                const versions = res.data ?? [];
                setAllVersions(versions);
                // Default to newest version (last in ascending list)
                setVersionIdx(Math.max(0, versions.length - 1));
            } catch {
                // Silently fall back to single-version view
                setAllVersions([]);
            }
        };
        void loadVersions();
    }, [requestId, model?.quoteVersion]);

    const searchParamsRef = useRef(searchParams);
    const setSearchParamsRef = useRef(setSearchParams);
    useEffect(() => {
        if (searchParamsRef.current.has("tab")) {
            const next = new URLSearchParams(searchParamsRef.current);
            next.delete("tab");
            setSearchParamsRef.current(next, {replace: true});
        }
    }, []);

    const isViewingLatest = allVersions.length === 0 || versionIdx === allVersions.length - 1;
    const displayedModel = isViewingLatest ? model : allVersions[versionIdx];

    // Reset editing mode when the user navigates to a different version
    React.useEffect(() => {
        setEditingRevision(false);
    }, [versionIdx]);

    const navigateToOverview = (forceLeave: boolean) => {
        if (forceLeave) {
            setSkipUnsavedCheck(true);
        }
        setTimeout(() => navigate(AppPaths.EXTERNAL_REQUEST_OVERVIEW, { replace: true }), 20);
    };

    const handleCancel = () => {
        navigateToOverview(false);
    };

    const handleQuoteSubmit = async () => {
        if (validate()) {
            await doSave();
        }
    };

    const doSave = async () => {
        // Disable unsaved changes blocker for this intentional navigation
        setSkipUnsavedCheck(true);
        if (model != null) {
            setProcessing(true);
            try {
                const quoteResult = await quoteApi.submitQuote(model);
                const newQuote = quoteResult.data;
                dispatch({type: "--set", data: newQuote});
                showMessage({
                    summary: t(RequestI18N.details.message.quoteSubmittedSuccess, {
                        boxhubNumber: model.requestDisplay?.boxhubNumber ?? t(CommonI18N.message.notGiven)
                    }),
                    severity: "success"
                });
                navigateToOverview(true);
            } catch (error: unknown) {
                setErrors(error as AxiosError);
            } finally {
                setProcessing(false);
            }
        }
    };

    // Dialog Handlers
    const handleSubmitQuote = () => {
        if (validate()) {
            showDialog((open, close) => (
                <ConfirmationDialog
                    open={open}
                    onClose={close}
                    closeOnConfirm
                    onConfirm={handleQuoteSubmit}
                    title={t(RequestI18N.details.action.sendQuote)}
                    message={t(RequestI18N.details.dialog.submitQuoteMessage)}
                    confirmButtonText={t(RequestI18N.details.action.confirm)}
                    cancelButtonText={t(CommonI18N.action.cancel)}
                />
            ));
        }
    };

    const handleRevokeRequest = () => {
        const doRevoke = async (close: () => void) => {
            setProcessing(true);
            try {
                await quoteApi.revokeRejectedRequestAsOfferor(model.requestDisplay.id!);
                showMessage({
                    summary: t(RequestI18N.details.message.requestRevokeSuccess, {
                        boxhubNumber: model.requestDisplay?.boxhubNumber
                    }),
                    severity: "success"
                });
                navigateToOverview(true);
            } catch (error) {
                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
            } finally {
                setProcessing(false);
                close();
            }
        };
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={() => doRevoke(close)}
                        title={t(RequestI18N.details.action.revokeRequest)}
                        message={t(RequestI18N.details.dialog.revokeRequestMessage)}
                        confirmButtonText={t(RequestI18N.details.action.confirm)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
            />
        ));
    };

    const handleRejectRequest = () => {
        const doReject = async (close: () => void) => {
            setProcessing(true);
            try {
                await quoteApi.rejectRequest({requestId: model.requestDisplay.id!});
                showMessage({
                    summary: t(RequestI18N.details.message.requestRejectedSuccess, {
                        boxhubNumber: model.requestDisplay?.boxhubNumber
                    }),
                    severity: "success"
                });
                navigateToOverview(true);
            } catch (error) {
                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
            } finally {
                setProcessing(false);
                close();
            }
        };
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={() => doReject(close)}
                        title={t(RequestI18N.details.action.rejectRequest)}
                        message={t(RequestI18N.details.dialog.rejectRequestMessage)}
                        confirmButtonText={t(RequestI18N.details.action.confirm)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
            />
        ));
    };

    const handleSubmitRevisedBid = () => {
        if (validate()) {
            showDialog((open, close) => (
                <ConfirmationDialog
                    open={open}
                    onClose={close}
                    closeOnConfirm
                    onConfirm={async () => {
                        setProcessing(true);
                        setSkipUnsavedCheck(true);
                        try {
                            await quoteApi.submitRevisedQuote(model.id!, model);
                            showMessage({
                                summary: t(RequestI18N.details.message.submitRevisedBidSuccess),
                                severity: "success"
                            });
                            navigateToOverview(true);
                        } catch (error) {
                            showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
                        } finally {
                            setProcessing(false);
                            close();
                        }
                    }}
                    title={t(RequestI18N.details.dialog.submitRevisedBidTitle)}
                    message={t(RequestI18N.details.dialog.submitRevisedBidMessage)}
                    confirmButtonText={t(RequestI18N.details.action.confirm)}
                    cancelButtonText={t(CommonI18N.action.cancel)}
                />
            ));
        }
    };

    const handleWithdrawQuote = () => {
        const doWithdraw = async (close: () => void) => {
            setSkipUnsavedCheck(true);
            if (!model.id) return;
            try {
                await quoteApi.withdrawQuote(model.id);
                showMessage({
                    summary: t(RequestI18N.details.message.withdrawQuoteSuccess),
                    severity: "success"
                });
                navigateToOverview(true);
            } catch (error) {
                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
            } finally {
                close();
            }
        };
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={() => doWithdraw(close)}
                        title={t(RequestI18N.details.dialog.withdrawQuoteTitle)}
                        message={t(RequestI18N.details.dialog.withdrawQuoteMessage)}
                        confirmButtonText={t(RequestI18N.details.action.confirm)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
            />
        ));
    };

    const handleOpenHistoryDialog = () => {
        if (requestId) {
            showDialog((open, close) => <EmailHistoryDialog open={open} requestId={requestId} onClose={close} />);
        }
    };

    const buttons = (
        <Grid2 container direction="row" spacing={2}>
            <ButtonEx
                startIcon={<ArrowBackIosIcon />}
                onClick={handleCancel}
                label={t(RequestI18N.details.action.backToOverview)}
            />
            <ButtonEx
                visible={!!requestId}
                startIcon={<BriefIcon mode={theme.palette.mode} />}
                onClick={handleOpenHistoryDialog}
                label={t(NotificationI18N.emailHistory.action.open)}
            />
        </Grid2>
    );

    const readonly =
        !isViewingLatest ||
        (model?.status !== QuoteStatus.VACANT && !(editingRevision && model?.status === QuoteStatus.IN_NEGOTIATION));
    const canStartRevision =
        !editingRevision &&
        model?.status === QuoteStatus.IN_NEGOTIATION &&
        model.requestDisplay?.requestActions?.includes(RequestActions.SUBMIT_REVISED_BID);
    const orderDataOrg = displayedModel?.requestDisplay?.orderData;

    const refreshOrder = () => {
        if (requestId) fetchOrder(requestId);
    };

    const cardGrid = (forceReadonly: boolean) => {
        const isCardReadonly = forceReadonly || readonly;
        return (
            <Grid2 container spacing={2} paddingTop={1} size={{ xs: 12 }}>
                <Grid2 container direction="column" spacing={2} size={{ sm: 12, md: 4 }} alignContent="flex-start">
                    <Grid2 size={{ xs: 12 }}>
                        <RequestCard model={displayedModel?.requestDisplay} mode="quote" />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <OrderDocumentsCard
                                orderDocuments={displayedModel?.orderDocuments}
                                readonly
                                additionalInformation={displayedModel?.requestDisplay?.additionalInformation}
                                mode="quote"
                                showAdditionalInformation
                                emphasized
                        />
                    </Grid2>
                </Grid2>
                <Grid2 container direction="column" spacing={2} size={{ sm: 12, md: 4 }} alignContent="flex-start">
                    <Grid2 size={{ xs: 12 }}>
                        <ProductCard
                            orderData={displayedModel?.orderData}
                            orgData={orderDataOrg}
                            onChange={onChange}
                            errors={errors}
                            mode="quote"
                            readonly={isCardReadonly}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <PalletizingCard
                            orderData={displayedModel?.orderData}
                            orgData={orderDataOrg}
                            onChange={onChange}
                            errors={errors}
                            mode="quote"
                            readonly={isCardReadonly}
                        />
                    </Grid2>
                </Grid2>
                <Grid2 container direction="column" spacing={2} size={{ sm: 12, md: 4 }} alignContent="flex-start">
                    <Grid2 size={{ xs: 12 }}>
                        <PricingCard
                            orderData={displayedModel?.orderData}
                            orgData={orderDataOrg}
                            onChange={onChange}
                            errors={errors}
                            dispatch={dispatch}
                            mode="quote"
                            readonly={isCardReadonly}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <QuoteCard
                            model={displayedModel}
                            errors={errors}
                            onChange={onChange}
                            readonly={isCardReadonly}
                            mode="quote"
                            showRejectReasonUnder="contact"
                            canStartRevision={!forceReadonly && !!canStartRevision}
                            onStartRevision={() => setEditingRevision(true)}
                        />
                    </Grid2>
                </Grid2>
            </Grid2>
        );
    };

    return (
        <AppFrame
            loading={status === "loading"}
            modelState={status}
            httpErrorStatus={httpErrorStatus}
            isDirty={!skipUnsavedCheck && isDirty}
            buttons={buttons}
        >
            {order ? (
                    <OrderBanner
                            order={order}
                            onStatusAdvanced={refreshOrder}
                            onCancelled={refreshOrder}
                            onDeliveryDateUpdated={refreshOrder}
                    />
            ) : (
                    <ExternalRequestBanner
                            displayedModel={displayedModel}
                            model={model}
                            processing={processing}
                            hasErrors={hasErrors}
                            editingRevision={editingRevision}
                            allVersions={allVersions}
                            versionIdx={versionIdx}
                            setVersionIdx={setVersionIdx}
                            onSubmitQuote={handleSubmitQuote}
                            onRejectRequest={handleRejectRequest}
                            onWithdrawQuote={handleWithdrawQuote}
                            onRevokeRequest={handleRevokeRequest}
                            onSubmitRevisedBid={handleSubmitRevisedBid}
                    />
            )}
            {cardGrid(false)}
            <DialogNode />
            {model?.requestDisplay?.id != null && <NotificationsWithPanel requestId={model.requestDisplay.id}/>}
        </AppFrame>
    );
};

export default withAuthWrapperComponent(ExternalRequestPage);
